// /pages/HR/components/charts/BarChart.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import '../charts/BarChart.css';

// Register Chart.js components including datalabels
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

/**
 * Reusable Bar Chart Component
 * Wrapper for Chart.js Bar chart with custom styling
 */
const BarChart = React.memo(({
  data = [],
  labels = [],
  title = '',
  xAxisLabel = '',
  yAxisLabel = '',
  horizontal = false,
  stacked = false,
  showLegend = false,
  showGrid = true,
  height = 300,
  loading = false,
  error = null,
  colors = null,
  onClick = null,
  onDataPointClick = null,
  animate = true,
  showValues = true,  // Changed default to true
  className = '',
  refreshInterval = null,
  sortBy = null,
}) => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef(null);

  // Process and sort data
  useEffect(() => {
    if (!data || data.length === 0) return;

    let processedData = [...data];
    let processedLabels = [...labels];

    // Sort data if requested
    if (sortBy) {
      const combined = processedLabels.map((label, index) => ({
        label,
        value: processedData[index]
      }));

      switch (sortBy) {
        case 'value-asc':
          combined.sort((a, b) => a.value - b.value);
          break;
        case 'value-desc':
          combined.sort((a, b) => b.value - a.value);
          break;
        case 'label-asc':
          combined.sort((a, b) => a.label.localeCompare(b.label));
          break;
        case 'label-desc':
          combined.sort((a, b) => b.label.localeCompare(a.label));
          break;
        default:
          break;
      }

      processedLabels = combined.map(item => item.label);
      processedData = combined.map(item => item.value);
    }

    // Handle colors
    let backgroundColors = colors;

    // If colors is an array with one color, apply it to all bars
    if (Array.isArray(colors) && colors.length === 1) {
      backgroundColors = new Array(processedData.length).fill(colors[0]);
    }
    // If colors is an array matching data length, use as-is
    else if (Array.isArray(colors) && colors.length === processedData.length) {
      backgroundColors = colors;
    }
    // If colors is not provided, use default gradient
    else if (!colors) {
      const ctx = chartRef.current?.ctx;
      if (ctx) {
        backgroundColors = processedData.map((_, index) => {
          const gradient = ctx.createLinearGradient(0, 0, 0, height);
          const baseColor = index % 2 === 0 ?
            { r: 91, g: 155, b: 213 } :  // Light blue
            { r: 46, g: 80, b: 144 };     // Dark blue

          gradient.addColorStop(0, `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.8)`);
          gradient.addColorStop(1, `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.3)`);
          return gradient;
        });
      } else {
        // Fallback colors if no context
        backgroundColors = [
          '#5B9BD5', '#4472C4', '#70AD47', '#FFC000',
          '#ED7D31', '#A5A5A5', '#E15759', '#70C0E8'
        ];
      }
    }

    setChartData({
      labels: processedLabels,
      datasets: [{
        label: title || 'Count',
        data: processedData,
        backgroundColor: backgroundColors,
        borderColor: Array.isArray(backgroundColors) ?
          backgroundColors.map(color =>
            typeof color === 'string' && color.startsWith('#') ?
              color : 'rgba(0,0,0,0.1)'
          ) : 'rgba(0,0,0,0.1)',
        borderWidth: 0,  // Removed borders for cleaner look
        borderRadius: 4,
        barThickness: 'flex',
        maxBarThickness: 40,
      }]
    });
  }, [data, labels, colors, height, sortBy, title]);

  // Auto-refresh functionality
  useEffect(() => {
    if (refreshInterval && !loading) {
      intervalRef.current = setInterval(() => {
        setIsRefreshing(true);
        // TODO: API INTEGRATION POINT
        setTimeout(() => setIsRefreshing(false), 500);
      }, refreshInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [refreshInterval, loading]);

  // Memoized click handler
  const handleDataPointClick = useCallback((event, elements) => {
    if (elements.length > 0 && onDataPointClick && chartData) {
      const element = elements[0];
      const datasetIndex = element.datasetIndex;
      const index = element.index;
      const label = chartData.labels[index];
      const value = chartData.datasets[datasetIndex].data[index];

      onDataPointClick({
        label,
        value,
        index,
        datasetIndex
      });
    } else if (onClick) {
      onClick(event);
    }
  }, [onDataPointClick, onClick, chartData]);

  // Memoized retry handler
  const handleRetry = useCallback(() => {
    // TODO: API INTEGRATION POINT
    console.log('Retrying chart data fetch...');
  }, []);

  // Chart configuration - memoized
  const options = useMemo(() => ({
    indexAxis: horizontal ? 'y' : 'x',
    responsive: true,
    maintainAspectRatio: false,
    animation: animate ? {
      duration: 1000,
      easing: 'easeOutQuart'
    } : false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        labels: {
          font: {
            family: 'Imprima, sans-serif',
            size: 12
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'rectRounded'
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(32, 56, 100, 0.75)',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 14,
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 6,
        titleFont: {
          family: 'Imprima, sans-serif',
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: 'Imprima, sans-serif',
          size: 13
        },
        footerFont: {
          family: 'Imprima, sans-serif',
          size: 11
        },
        callbacks: {
          title: function (context) {
            return context[0].label || '';
          },
          label: function (context) {
            let label = context.dataset.label || 'Requests';
            // For horizontal charts, value is on x-axis
            const value = horizontal ? context.parsed.x : context.parsed.y;
            return `${label}: ${new Intl.NumberFormat('en-US').format(value)}`;
          },
          footer: function (context) {
            const total = context[0].dataset.data.reduce((a, b) => a + b, 0);
            // For horizontal charts, value is on x-axis
            const value = horizontal ? context[0].parsed.x : context[0].parsed.y;
            const percentage = ((value / total) * 100).toFixed(1);
            return `${percentage}% of total`;
          }
        }
      },
      datalabels: {
        display: showValues,
        anchor: horizontal ? 'end' : 'end',
        align: horizontal ? 'right' : 'top',
        offset: 4,
        color: '#374151',
        font: {
          family: 'Imprima, sans-serif',
          size: 12,
          weight: '600'
        },
        formatter: (value) => {
          return value; // Show raw value, no formatting for cleaner look
        },
        clip: false  // Ensure labels aren't clipped
      }
    },
    scales: {
      x: {
        type: horizontal ? 'linear' : 'category',  // Linear for horizontal, category for vertical
        stacked: stacked,
        beginAtZero: horizontal ? true : undefined,
        grid: {
          display: horizontal ? showGrid : (showGrid && !horizontal),
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: horizontal ? {
          // X-axis ticks for horizontal (numeric values)
          font: {
            family: 'Imprima, sans-serif',
            size: 11
          },
          color: '#6B7280',
          callback: function (value) {
            return new Intl.NumberFormat('en-US', {
              notation: 'compact',
              compactDisplay: 'short'
            }).format(value);
          }
        } : {
          // X-axis ticks for vertical (categories)
          font: {
            family: 'Imprima, sans-serif',
            size: 10
          },
          color: '#6B7280',
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          padding: 5,
          callback: function (value, index) {
            const label = this.getLabelForValue(value);
            if (label && label.length > 20) {
              return label.substring(0, 17) + '...';
            }
            return label;
          }
        },
        title: {
          display: !!xAxisLabel,
          text: xAxisLabel,
          font: {
            family: 'Imprima, sans-serif',
            size: 12,
            weight: 'bold'
          },
          color: '#374151'
        }
      },
      y: {
        type: horizontal ? 'category' : 'linear',  // Category for horizontal, linear for vertical
        stacked: stacked,
        beginAtZero: horizontal ? undefined : true,
        grid: {
          display: horizontal ? (showGrid && horizontal) : showGrid,
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0.05)',
          lineWidth: 1
        },
        ticks: horizontal ? {
          // Y-axis ticks for horizontal (categories - names)
          font: {
            family: 'Imprima, sans-serif',
            size: 11
          },
          color: '#374151',
          autoSkip: false,
          padding: 5
        } : {
          // Y-axis ticks for vertical (numeric values)
          font: {
            family: 'Imprima, sans-serif',
            size: 11
          },
          color: '#6B7280',
          callback: function (value) {
            if (value === 0) return '0';
            return new Intl.NumberFormat('en-US', {
              notation: 'compact',
              compactDisplay: 'short'
            }).format(value);
          }
        },
        title: {
          display: !!yAxisLabel,
          text: yAxisLabel,
          font: {
            family: 'Imprima, sans-serif',
            size: 12,
            weight: 'bold'
          },
          color: '#374151'
        }
      }
    },
    onClick: handleDataPointClick
  }), [
    horizontal, animate, showLegend, showGrid, showValues,
    stacked, xAxisLabel, yAxisLabel, handleDataPointClick
  ]);

  // Loading state
  if (loading) {
    return (
      <div className={`bar-chart-container loading ${className}`} style={{ height }}>
        <div className="chart-skeleton">
          <div className="skeleton-bars">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="skeleton-bar"
                style={{
                  height: `${Math.random() * 60 + 20}%`,
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`bar-chart-container error ${className}`} style={{ height }}>
        <div className="chart-error">
          <span className="error-icon">📊</span>
          <p className="error-message">{error}</p>
          <button
            className="retry-button"
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!chartData || chartData.datasets[0].data.length === 0) {
    return (
      <div className={`bar-chart-container no-data ${className}`} style={{ height }}>
        <div className="no-data-message">
          <span className="no-data-icon">📊</span>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bar-chart-container ${className}`} style={{ height }}>
      {isRefreshing && <div className="chart-refresh-indicator" />}
      <Bar
        ref={chartRef}
        data={chartData}
        options={options}
      />
    </div>
  );
});

BarChart.displayName = 'BarChart';

export default BarChart;